// OfflineBanner.jsx — Offline-Status + Queue-Puffer Anzeige
import React, { useState, useEffect, useRef } from 'react';
import './OfflineBanner.css';
import { getPendingCount, replayAll } from '../utils/offlineQueue';

export default function OfflineBanner({ swRegistration }) {
  const [isOffline, setIsOffline]             = useState(!navigator.onLine);
  const [pendingCount, setPendingCount]       = useState(0);
  const [syncState, setSyncState]             = useState(null); // null | 'syncing' | 'done' | 'partial'
  const [syncDetail, setSyncDetail]           = useState(null); // { replayed, failed }
  const [showReconnected, setShowReconnected] = useState(false);
  const [showUpdate, setShowUpdate]           = useState(false);
  const [pendingReg, setPendingReg]           = useState(null);
  const wasOfflineRef = useRef(false);
  const syncTimerRef  = useRef(null);

  // ── Queue-Count aktuell halten ──────────────────────────────────────────────
  const refreshCount = async () => {
    try {
      const n = await getPendingCount();
      setPendingCount(n);
    } catch (_) {}
  };

  useEffect(() => {
    refreshCount();
    const handler = () => refreshCount();
    window.addEventListener('offline-queue-changed', handler);
    return () => window.removeEventListener('offline-queue-changed', handler);
  }, []);

  // ── Online/Offline Events ───────────────────────────────────────────────────
  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      wasOfflineRef.current = true;
    };
    const handleOnline = async () => {
      setIsOffline(false);
      const count = await getPendingCount();
      if (count > 0) {
        // Ausstehende Requests automatisch nachsenden
        setSyncState('syncing');
        try {
          const result = await replayAll();
          setSyncDetail(result);
          setSyncState(result.failed > 0 ? 'partial' : 'done');
        } catch (_) {
          setSyncState('partial');
          setSyncDetail({ replayed: 0, failed: count });
        }
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
          setSyncState(null);
          setSyncDetail(null);
        }, 4000);
        refreshCount();
      } else if (wasOfflineRef.current) {
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3500);
      }
      wasOfflineRef.current = false;
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online',  handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online',  handleOnline);
    };
  }, []);

  // ── Sync-Events (von offlineQueue.replayAll) ────────────────────────────────
  useEffect(() => {
    const onSyncing = (e) => {
      setSyncState('syncing');
      setSyncDetail({ total: e.detail?.total });
    };
    const onSynced = (e) => {
      setSyncDetail(e.detail);
      setSyncState(e.detail?.failed > 0 ? 'partial' : 'done');
      refreshCount();
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        setSyncState(null);
        setSyncDetail(null);
      }, 4000);
    };
    window.addEventListener('offline-queue-syncing', onSyncing);
    window.addEventListener('offline-queue-synced',  onSynced);
    return () => {
      window.removeEventListener('offline-queue-syncing', onSyncing);
      window.removeEventListener('offline-queue-synced',  onSynced);
    };
  }, []);

  // ── Service-Worker Update-Event ─────────────────────────────────────────────
  useEffect(() => {
    if (!swRegistration) return;
    setPendingReg(swRegistration);
    setShowUpdate(true);
  }, [swRegistration]);

  const applyUpdate = () => {
    if (pendingReg?.waiting) pendingReg.waiting.postMessage({ type: 'SKIP_WAITING' });
    setShowUpdate(false);
  };

  const isVisible = isOffline || showReconnected || showUpdate || syncState !== null || pendingCount > 0;
  if (!isVisible) return null;

  return (
    <div className="offline-banner-container">

      {/* Update-Banner */}
      {showUpdate && !isOffline && (
        <div className="offline-banner update-banner">
          <span>🔄 Neue Version verfügbar</span>
          <button className="offline-action-btn" onClick={applyUpdate}>
            Jetzt aktualisieren
          </button>
          <button className="offline-close-btn" onClick={() => setShowUpdate(false)}>✕</button>
        </div>
      )}

      {/* Sync läuft gerade */}
      {syncState === 'syncing' && (
        <div className="offline-banner syncing-banner">
          <span className="offline-sync-spinner" />
          <span>
            Synchronisiere{syncDetail?.total ? ` ${syncDetail.total}` : ''} ausstehende Wertung{syncDetail?.total !== 1 ? 'en' : ''}…
          </span>
        </div>
      )}

      {/* Sync abgeschlossen */}
      {syncState === 'done' && syncDetail && (
        <div className="offline-banner synced-banner">
          <span>✅ {syncDetail.replayed} Wertung{syncDetail.replayed !== 1 ? 'en' : ''} synchronisiert</span>
        </div>
      )}

      {/* Sync teilweise fehlgeschlagen */}
      {syncState === 'partial' && syncDetail && (
        <div className="offline-banner sync-partial-banner">
          <span>⚠️ Sync: {syncDetail.replayed} ok, {syncDetail.failed} fehlgeschlagen</span>
        </div>
      )}

      {/* Offline-Banner mit Queue-Count */}
      {isOffline && (
        <div className="offline-banner offline-active">
          <span className="offline-icon">📵</span>
          <div className="offline-text">
            <strong>Kein Internet</strong>
            {pendingCount > 0
              ? <span>{pendingCount} Wertung{pendingCount !== 1 ? 'en' : ''} ausstehend – werden sync'd</span>
              : <span>Wertungen werden lokal gespeichert</span>
            }
          </div>
          {pendingCount > 0 && (
            <span className="offline-queue-badge">{pendingCount}</span>
          )}
        </div>
      )}

      {/* Wieder-Online (nur wenn nichts sync'd werden musste) */}
      {showReconnected && !isOffline && syncState === null && (
        <div className="offline-banner reconnected-banner">
          <span>✅ Wieder online</span>
        </div>
      )}

    </div>
  );
}
